/**
 * @prop --background-color: Background color
 * @prop --image-background-color: Background color of image
 *
 * @prop --stzh-pagetitle-disturber-height: Global: Disturber height
 * @prop --stzh-pagetitle-disturber-stuck-height: Global: Disturber height when sticky (below medium breakpoint)
 * @prop --stzh-pagetitle-disturber-is-stuck: **Global**: Whether disturber is currently sticky (readonly variable on `<html>`)
 * @prop --stzh-pagetitle-disturber-is-not-stuck: **Global**: Whether disturber is currently not sticky (readonly variable on `<html>`)
 * @prop --stzh-pagetitle-background-color: **Global**: Background color of pagetitle.
 */

:host {
  --background-color: var(--stzh-pagetitle-background-color, #{$pagetitleBackgroundColor});
  --image-background-color: #{$ratioBackgroundColor};
  --heading-color: var(--stzh-pagetitle-heading-color, #{$headingColor});
  --lead-color: var(--stzh-pagetitle-lead-color, #{$headingColor});

  ::slotted([slot="image"]) {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ::slotted([slot="readspeaker"]) * {
    --color: var(--stzh-pagetitle-readspeaker-color, #{$headingColor});
    --hover-color: var(--stzh-pagetitle-readspeaker-color, #{$headingColor});
  }

  &[background="default"] {
    --stzh-readspeaker-background-color: var(--background-color);
    --stzh-readspeaker-audio-track-progress-background-color: #{$colorWhite};
  }

  &[background="transparent"] {
    --background-color: transparent;
  }
}

.stzh-pagetitle {
  background-color: var(--background-color);

  &__container {
    @include container;
  }

  &__wrapper {
    position: relative;
    display: grid;
  }

  &__text {
    @include spaceCurve('padding-top', 'big');
    @include spaceCurve('padding-bottom', 'big');
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__image-wrapper {
    position: relative;
  }

  &__ratio {
    --background-color: var(--image-background-color);
  }

  &__image-inner-wrapper {
    background-color: var(--image-background-color);
    position: absolute;
    inset: 0;
    height: 100%;
    margin-left: calc(#{$containerMargin} / -1);
    margin-right: calc(#{$containerMargin} / -1);

    @include mq($from: small) {
      margin-left: calc(#{$containerMarginSmall} / -1);
      margin-right: calc(#{$containerMarginSmall} / -1);
    }

    @include mq($from: medium) {
      margin-left: 0;
      margin-right: 0;
      width: calc(100% + #{$containerMarginMedium});
    }

    @include mq($from: large) {
      width: calc(100% + #{$containerMarginLarge});
    }

    @include mq($from: ultra) {
      width: calc(100% + ((#{map-get($breakpoints, 'ultra')} - #{$containerMaxWidth}) / 2));
    }
  }

  &__meta {
    display: flex;
    flex-direction: column-reverse;
    row-gap: space('xsmall');

    @include mq($from: medium) {
      flex-direction: row;
    }
  }

  &__tag {
    &:not(:empty) {
      margin-right: auto;
    }
  }

  &__readspeaker {
    @include mq($from: small, $to: medium) {
      --stzh-readspeaker-dropdown-position: relative;
      --stzh-readspeaker-dropdown-top: auto;
    }
  }

  &__dateline {
    @include fontSize('milli');
    @include spaceCurve('margin-top', 'regular');
    color: $colorPrimary70;
    margin-bottom: space('xsmall');
  }

  &__lead {
    --stzh-base-lead-color: var(--lead-color);
  }

  &__heading {
    --stzh-heading-color: var(--heading-color);
  }

  &__heading:not(:empty) + &__lead:not(:empty) {
    @include spaceCurve('margin-top', 'medium');
  }

  &__disturber {
    z-index: $zIndexSticky;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;

    @include mq($from: medium) {
      z-index: initial;
      position: absolute;
      width: calc(((100% - 7 * #{$gridGutterMedium}) / 8 * 3) + 2 * #{$gridGutterMedium});
      left: auto;
      bottom: calc((#{spaceCurveValue('huge', 'medium')} - #{spaceCurveValue('regular', 'medium')}) / -1);
    }

    @include mq($from: large) {
      width: calc(((100% - 11 * #{$gridGutterLarge}) / 12 * 4) + 3 * #{$gridGutterLarge});
      // bottom: calc(#{spaceCurveValue('huge', 'large')} / -1);
    }

    @include mq($from: ultra) {
      width: calc(((100% - 11 * #{$gridGutterUltra}) / 12 * 4) + 3 * #{$gridGutterUltra});
    }
  }

  /* Has no image variant */

  &--has-no-image &__ratio {
    display: none;
  }

  &--has-no-image &__text {
    @include grid;
  }

  &--has-no-image &__meta,
  &--has-no-image &__dateline,
  &--has-no-image &__heading,
  &--has-no-image &__lead {
    grid-column: 1 / span 4;
  }

  &--has-no-image &__meta,
  &--has-no-image &__dateline,
  &--has-no-image &__heading {
    @include mq($from: medium) {
      grid-column: 1 / span 8;
    }
  }

  &--has-no-image &__lead {
    @include mq($from: medium) {
      grid-column: 1 / span 6;
    }

    @include mq($from: large) {
      grid-column: 1 / span 7;
    }
  }

  /* Has image variant */

  &--has-image &__wrapper {
    @include mq($from: medium) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: space('xxlarge');
    }
  }

  /* Has meta */

  &--has-meta &__text {
    @include spaceCurve('padding-top', 'regular');
  }

  /* Has tag */

  &--has-tag &__readspeaker {
    @include mq($from: medium) {
      --stzh-readspeaker-dropdown-left: auto;
      --stzh-readspeaker-dropdown-right: 0px;

      --stzh-readspeaker-dropdown-settings-left: auto;
      --stzh-readspeaker-dropdown-settings-right: 0px;
    }
  }

  &--has-tag:not(&--has-dateline) &__meta {
    margin-bottom: space('xlarge');
  }

  /* Default background */

  &--background-default &__text {
    --stzh-selection-background-color: #{$colorWhite};
  }

  /* Transparent background */

  &--background-transparent &__text {
    @include spaceCurve('padding-bottom', 'large');
  }

  /* Hyphens */
  &--hyphens &__heading {
    hyphens: auto;
  }

  /* Has disturber */
  &--has-disturber {
    @include spaceCurve('margin-bottom', 'section-inner');
  }

  &--has-disturber#{&}--has-no-image &__lead {
    @include mq($from: medium, $to: large) {
      grid-column: 1 / span 5;
    }
  }
}
